import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logout } from '../../redux/auth/authSlice';
import { cn } from '../../lib/utils';

// ナビゲーションリンクの共通スタイル（アクティブ状態で強調表示）
const navLinkClass = (active?: boolean) =>
  cn(
    'flex items-center px-5 py-3 text-inherit no-underline transition-colors hover:bg-white/[0.08] hover:text-inherit',
    active ? 'bg-white/[0.12] font-semibold' : 'font-normal',
  );

// 管理者サイドバーのタブ型定義
export type AdminTab = 'users' | 'agents' | 'aiAnalysisModels' | 'adAccounts' | 'statistics' | 'organizations' | 'categories' | 'campaigns' | 'imageStyles' | 'styleQuality' | 'imageStyleCategories' | 'materialStyles' | 'materialStyleCategories' | 'carouselStyles' | 'promptTemplates' | 'bannerPromptReports' | 'samplePrompts' | 'videoTemplates' | 'ugcPresets' | 'magicResizePresets' | 'magicResizeDebug' | 'mangaStyles' | 'musicStyles' | 'news' | 'helpArticles' | 'tutorials' | 'externalApiTokens' | 'bigQueryHandoff' | 'partnerPortal' | 'behavior' | 'videoStyleRecipes';

const tabToSlug: Record<AdminTab, string> = {
  users: 'users',
  agents: 'agents',
  aiAnalysisModels: 'ai-analysis-models',
  adAccounts: 'ad-accounts',
  organizations: 'organizations',
  categories: 'categories',
  campaigns: 'campaigns',
  imageStyles: 'image-styles',
  styleQuality: 'style-quality',
  imageStyleCategories: 'style-categories',
  materialStyles: 'material-styles',
  materialStyleCategories: 'material-style-categories',
  carouselStyles: 'carousel-styles',
  promptTemplates: 'prompt-templates',
  bannerPromptReports: 'banner-prompt-reports',
  samplePrompts: 'sample-prompts',
  mangaStyles: 'manga-styles',
  musicStyles: 'music-styles',
  videoTemplates: 'video-templates',
  ugcPresets: 'ugc-presets',
  videoStyleRecipes: 'video-style-recipes',
  magicResizePresets: 'resize-presets',
  magicResizeDebug: 'resize-debug',
  statistics: 'statistics',
  news: 'news',
  helpArticles: 'help-articles',
  tutorials: 'tutorials',
  externalApiTokens: 'external-api-tokens',
  bigQueryHandoff: 'bigquery-handoff',
  partnerPortal: 'partner-portal',
  behavior: 'behavior',
};

interface AdminSidebarProps {
  activePage: AdminTab;
  onPageChange: (page: AdminTab) => void;
  adminName: string;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activePage,
  onPageChange,
  adminName,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="fixed left-0 top-0 z-[100] flex h-screen w-[250px] min-w-[250px] max-w-[250px] shrink-0 flex-col bg-dark py-4 text-white">
      <div className="mb-6 px-5 py-4 text-xl font-bold">管理パネル</div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Link
          to={`/admin/${tabToSlug.users}`}
          className={navLinkClass(activePage === 'users')}
        >
          <span className="mr-2.5 text-lg">👥</span>ユーザー管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.agents}`}
          className={navLinkClass(activePage === 'agents')}
        >
          <span className="mr-2.5 text-lg">🤖</span>AIエージェント管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.aiAnalysisModels}`}
          className={navLinkClass(activePage === 'aiAnalysisModels')}
        >
          <span className="mr-2.5 text-lg">🧠</span>AI解析モデル設定
        </Link>
        <Link
          to={`/admin/${tabToSlug.adAccounts}`}
          className={navLinkClass(activePage === 'adAccounts')}
        >
          <span className="mr-2.5 text-lg">📣</span>広告アカウント
        </Link>
        <Link
          to={`/admin/${tabToSlug.organizations}`}
          className={navLinkClass(activePage === 'organizations')}
        >
          <span className="mr-2.5 text-lg">🏢</span>組織管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.categories}`}
          className={navLinkClass(activePage === 'categories')}
        >
          <span className="mr-2.5 text-lg">📂</span>カテゴリ管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.imageStyles}`}
          className={navLinkClass(activePage === 'imageStyles')}
        >
          <span className="mr-2.5 text-lg">🎨</span>画像スタイル管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.styleQuality}`}
          className={navLinkClass(activePage === 'styleQuality')}
        >
          <span className="mr-2.5 text-lg">📊</span>スタイル品質
        </Link>
        <Link
          to={`/admin/${tabToSlug.imageStyleCategories}`}
          className={navLinkClass(activePage === 'imageStyleCategories')}
        >
          <span className="mr-2.5 text-lg">🏷️</span>スタイルカテゴリ管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.materialStyles}`}
          className={navLinkClass(activePage === 'materialStyles')}
        >
          <span className="mr-2.5 text-lg">🖼️</span>素材スタイル管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.materialStyleCategories}`}
          className={navLinkClass(activePage === 'materialStyleCategories')}
        >
          <span className="mr-2.5 text-lg">📁</span>素材カテゴリ管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.carouselStyles}`}
          className={navLinkClass(activePage === 'carouselStyles')}
        >
          <span className="mr-2.5 text-lg">🎠</span>カルーセルスタイル管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.promptTemplates}`}
          className={navLinkClass(activePage === 'promptTemplates')}
        >
          <span className="mr-2.5 text-lg">📝</span>プロンプト管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.bannerPromptReports}`}
          className={navLinkClass(activePage === 'bannerPromptReports')}
        >
          <span className="mr-2.5 text-lg">📈</span>バナープロンプト分析
        </Link>
        <Link
          to={`/admin/${tabToSlug.samplePrompts}`}
          className={navLinkClass(activePage === 'samplePrompts')}
        >
          <span className="mr-2.5 text-lg">💬</span>サンプルプロンプト管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.mangaStyles}`}
          className={navLinkClass(activePage === 'mangaStyles')}
        >
          <span className="mr-2.5 text-lg">📖</span>漫画スタイル管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.musicStyles}`}
          className={navLinkClass(activePage === 'musicStyles')}
        >
          <span className="mr-2.5 text-lg">🎵</span>音楽スタイル管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.videoTemplates}`}
          className={navLinkClass(activePage === 'videoTemplates')}
        >
          <span className="mr-2.5 text-lg">🎬</span>動画テンプレート管理
        </Link>
        <Link to={`/admin/${tabToSlug.ugcPresets}`} className={navLinkClass(activePage === 'ugcPresets')}>
          <span className="mr-2.5 text-lg">🧑</span>UGC 出演者・場所
        </Link>
        <Link to={`/admin/${tabToSlug.videoStyleRecipes}`} className={navLinkClass(activePage === 'videoStyleRecipes')}>
          <span className="mr-2.5 text-lg">🎞️</span>動画スタイル
        </Link>
        <Link
          to={`/admin/${tabToSlug.magicResizePresets}`}
          className={navLinkClass(activePage === 'magicResizePresets')}
        >
          <span className="mr-2.5 text-lg">📐</span>リサイズプリセット管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.magicResizeDebug}`}
          className={navLinkClass(activePage === 'magicResizeDebug')}
        >
          <span className="mr-2.5 text-lg">🔍</span>リサイズデバッグ
        </Link>
        <Link
          to={`/admin/${tabToSlug.news}`}
          className={navLinkClass(activePage === 'news')}
        >
          <span className="mr-2.5 text-lg">📰</span>ニュース管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.helpArticles}`}
          className={navLinkClass(activePage === 'helpArticles')}
        >
          <span className="mr-2.5 text-lg">📚</span>ヘルプ記事管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.tutorials}`}
          className={navLinkClass(activePage === 'tutorials')}
        >
          <span className="mr-2.5 text-lg">🔦</span>チュートリアル
        </Link>
        <Link
          to={`/admin/${tabToSlug.externalApiTokens}`}
          className={navLinkClass(activePage === 'externalApiTokens')}
        >
          <span className="mr-2.5 text-lg">🔑</span>外部連携API
        </Link>
        <Link
          to={`/admin/${tabToSlug.bigQueryHandoff}`}
          className={navLinkClass(activePage === 'bigQueryHandoff')}
        >
          <span className="mr-2.5 text-lg">🗄️</span>BI連携
        </Link>
        <Link
          to={`/admin/${tabToSlug.partnerPortal}`}
          className={navLinkClass(activePage === 'partnerPortal')}
        >
          <span className="mr-2.5 text-lg">🤝</span>パートナー管理
        </Link>
        <Link
          to={`/admin/${tabToSlug.campaigns}`}
          className={navLinkClass(activePage === 'campaigns')}
        >
          <span className="mr-2.5 text-lg">📣</span>キャンペーン
        </Link>
        <Link
          to={`/admin/${tabToSlug.behavior}`}
          className={navLinkClass(activePage === 'behavior')}
        >
          <span className="mr-2.5 text-lg">🧭</span>行動分析
        </Link>
        <Link
          to={`/admin/${tabToSlug.statistics}`}
          className={navLinkClass(activePage === 'statistics')}
        >
          <span className="mr-2.5 text-lg">📊</span>統計情報
        </Link>
      </div>

      <div className="border-t border-white/[0.12] pt-4">
        {/*
        <div className={navLinkClass()} onClick={() => navigate('/admin/profile')}>
          <span className="mr-2.5 text-lg">👤</span>プロフィール
        </div>
        <div className={navLinkClass()} onClick={() => navigate('/admin/settings')}>
          <span className="mr-2.5 text-lg">⚙️</span>設定
        </div>
        */}
        <div className={cn(navLinkClass(), 'cursor-pointer')} onClick={handleLogout}>
          <span className="mr-2.5 text-lg">🚪</span>ログアウト
        </div>
      </div>

      <div className="flex flex-col px-5 py-4 text-sm">
        <div className="font-semibold">{adminName}</div>
        <div className="mt-1 text-xs text-white/60">管理者</div>
      </div>
    </div>
  );
};

export default AdminSidebar;
